import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Connection } from '../types';

/* ─── View Connection ─── */
interface ConnViewDialogProps {
	conn: Connection;
	open: boolean;
	onClose: () => void;
}

export function ConnViewDialog({ conn, open, onClose }: ConnViewDialogProps) {
	const rows: [string, string, string][] = [
		['Client', conn.clientName || '—', 'text'],
		['Provider', conn.provider, 'text'],
		['Label', conn.label, 'text'],
		['Type', conn.type, 'badge'],
		['Status', conn.status, 'badge'],
		['Region', conn.region || 'Global', 'text'],
		['Credential', conn.cred, 'mono'],
		['From email', 'noreply@fyscaltech.com', 'text'],
		['From name', 'Fyscal Technologies', 'text'],
		['Environment', 'Production', 'text'],
		['Created', conn.created, 'text'],
		[
			'Routing usage',
			conn.status === 'Active'
				? 'Email Fallback Chain · Position 1'
				: 'Not in any routing config',
			'routing',
		],
		['Last successful send', 'May 20, 2026 10:41 AM', 'muted'],
	];

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>View Connection</DialogTitle>
				</DialogHeader>
				<div className="space-y-1">
					{rows.map(([key, value, type]) => (
						<div
							key={key}
							className="flex items-center border-b py-2.5 text-sm"
						>
							<span className="w-40 shrink-0 text-muted-foreground">{key}</span>
							{type === 'badge' ? (
								<Badge
									variant={
										value === 'Active'
											? 'default'
											: value === 'Error'
												? 'destructive'
												: 'secondary'
									}
								>
									{value}
								</Badge>
							) : type === 'mono' ? (
								<span className="font-mono text-sm">{value}</span>
							) : type === 'muted' ? (
								<span className="text-muted-foreground">{value}</span>
							) : (
								<span>{value || 'N/A'}</span>
							)}
						</div>
					))}
				</div>
				<div className="flex justify-end">
					<Button onClick={onClose}>Close</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

/* ─── Edit Connection ─── */
interface ConnEditDialogProps {
	conn: Connection;
	open: boolean;
	onClose: () => void;
}

export function ConnEditDialog({ conn, open, onClose }: ConnEditDialogProps) {
	const fields = [
		{ label: 'Label', value: conn.label },
		{ label: 'From Email', value: 'noreply@fyscaltech.com' },
		{ label: 'From Name', value: 'Fyscal Technologies' },
		{ label: 'Region', value: conn.region || 'Global' },
	];

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						Edit Connection {conn.provider} · {conn.label}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					{fields.map((f) => (
						<div key={f.label}>
							<Label>{f.label}</Label>
							<Input defaultValue={f.value} className="mt-1" />
						</div>
					))}
					<div className="flex items-center gap-2 rounded border bg-muted/30 p-3 text-xs text-muted-foreground">
						<span>🔒</span>
						API keys and credentials cannot be edited. To update credentials,
						delete this connection and create a new one.
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={onClose}>Save changes</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

/* ─── Test Connection ─── */
interface ConnTestDialogProps {
	conn: Connection;
	open: boolean;
	onClose: () => void;
}

export function ConnTestDialog({ conn, open, onClose }: ConnTestDialogProps) {
	const [state, setState] = useState<'idle' | 'testing' | 'ok' | 'fail'>(
		'idle',
	);

	useEffect(() => {
		if (open) {
			setState('testing');
			const t = setTimeout(
				() => setState(conn.status === 'Error' ? 'fail' : 'ok'),
				1600,
			);
			return () => clearTimeout(t);
		}
	}, [open, conn.status]);

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						Test Connection {conn.provider} · {conn.label}
					</DialogTitle>
				</DialogHeader>
				<div className="flex flex-col items-center gap-4 py-4">
					{state === 'testing' && (
						<>
							<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
							<p className="text-sm text-muted-foreground">
								Testing credentials… Firing a live validation request to{' '}
								{conn.provider}
							</p>
						</>
					)}
					{state === 'ok' && (
						<>
							<CheckCircle2 className="h-8 w-8 text-green-500" />
							<p className="text-sm">
								Connection successful — credentials are valid and the provider
								responded correctly.
							</p>
						</>
					)}
					{state === 'fail' && (
						<>
							<XCircle className="h-8 w-8 text-red-500" />
							<p className="text-sm">
								Connection failed — the provider returned an authentication
								error. Check your credentials.
							</p>
						</>
					)}
				</div>
				<div className="flex justify-end">
					<Button onClick={onClose}>Close</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
